import { createAction, createSlice } from '@reduxjs/toolkit';

// Initial state with only DCR Report specific data
export const dcrReportInitialState = {
  // Filter values
  filterValues: {
    divisionId: [],
    monthYearData: [],
    hqId: [],
    roleId: [],
    staffId: null
  },

  // Division data
  divisionLoading: false,
  divisionData: [],
  divisionError: false,
  divisionMsg: '',

  // Staff data
  staffLoading: false,
  staffData: [],
  staffError: false,
  staffMsg: '',
  selectedStaff: null,

  // Month/Year data
  monthYearLoading: false,
  monthYearData: [],
  monthYearError: false,
  monthYearMsg: '',

  // Years data
  yearsData: [],

  // DCR Report main data
  dcrReportData: {
    row: []  // Changed from array to object with row property
  },
  loading: false,
  error: false,
  tableMessage: false,

  // Calendar response and error properties
  calendarResponse: null,
  calendarError: null,

  // DCR Day Report data
  dcrDayReportLoading: false,
  dcrDayReport: { screen: 1 },
  dcrDayReportError: false,
  dcrDayReportMsg: '',
};

// Action creators for DCR Report
export const fetchDivisionDataCreator = createAction(
  'DCR_REPORT/FETCH_DIVISION_DATA'
);

export const fetchStaffDataCreator = createAction<any>(
  'DCR_REPORT/FETCH_STAFF_DATA'
);

export const fetchDcrReportDataCreator = createAction<any>(
  'DCR_REPORT/FETCH_DCR_REPORT_DATA'
);
export const fetchDailyPlanStatusDcrCreator = createAction<any>(
  'DCR_REPORT/FETCH_DAILY_PLAN_STATUS_DCR'
);

export const fetchDcrDayReportDataCreator = createAction<{
  dcrDate: string;
  staffPositionId: string;
  staffUserId: string;
}>('DCR_REPORT/FETCH_DCR_DAY_REPORT');

// Create the slice
const dcrReportSlice = createSlice({
  name: 'dcrReport',
  initialState: dcrReportInitialState,
  reducers: {
    // Filter values
    setFilterValues: (state, action) => {
      state.filterValues = {
        ...state.filterValues,
        ...action.payload
      };
    },
    resetFilterValues: (state) => {
      state.filterValues = dcrReportInitialState.filterValues;
    },

    // Division reducers
    setDivisionLoading: (state) => {
      state.divisionLoading = true;
      state.divisionData = [];
      state.divisionError = false;
      state.divisionMsg = '';
    },
    setDivisionData: (state, action) => {
      state.divisionLoading = false;
      state.divisionData = action.payload;
      state.divisionError = false;
    },
    setDivisionError: (state, action) => {
      state.divisionLoading = false;
      state.divisionData = [];
      state.divisionError = true;
      state.divisionMsg = action.payload || 'Error loading divisions';
    },

    // Staff reducers
    setStaffLoading: (state) => {
      state.staffLoading = true;
      state.staffData = [];
      state.staffError = false;
      state.staffMsg = '';
    },
    setStaffData: (state, action) => {
      state.staffLoading = false;
      state.staffData = action.payload;
      state.staffError = false;
    },
    setStaffError: (state, action) => {
      state.staffLoading = false;
      state.staffData = [];
      state.staffError = true;
      state.staffMsg = action.payload || 'Error loading staff';
    },
    setSelectedStaffData: (state, action) => {
      state.selectedStaff = action.payload;
    },
     setCalendarResponse(state, action) {
      state.calendarResponse = action.payload;
    },
    setCalendarError(state, action) {
      state.calendarError = action.payload;
    },

    // Month/Year reducers
    setMonthYearLoading: (state) => {
      state.monthYearLoading = true;
      state.monthYearData = [];
      state.monthYearError = false;
      state.monthYearMsg = '';
    },
    setMonthYearData: (state, action) => {
      state.monthYearLoading = false;
      state.monthYearData = action.payload;
      state.monthYearError = false;
    },
    setMonthYearError: (state, action) => {
      state.monthYearLoading = false;
      state.monthYearData = [];
      state.monthYearError = true;
      state.monthYearMsg = action.payload || 'Error loading month/year data';
    },

    // Years data
    setYearsValues: (state, action) => {
      state.yearsData = action.payload;
    },

    // DCR Report data reducers
    setDcrReportData: (state, action) => {
      state.dcrReportData = action.payload;
      state.loading = false;
      state.error = false;
    },
    setDcrReportLoading: (state) => {
      state.loading = true;
      state.error = false;
    },
    setDcrReportError: (state) => {
      state.loading = false;
      state.error = true;
      state.dcrReportData = { row: [] };
    },
    setTableMessage: (state, action) => {
      state.tableMessage = action.payload;
    },

    // DCR Day Report reducers
   setDcrDayReportLoading: (state) => {
  state.dcrDayReportLoading = true;
  state.dcrDayReportError = false;
  state.dcrDayReportMsg = '';
  // Always keep screen property for UI logic
  if (!state.dcrDayReport) {
    state.dcrDayReport = { screen: 1 };
  } else {
    state.dcrDayReport.screen = 1;
  }
},
setDcrDayReportData: (state, action) => {
  state.dcrDayReportLoading = false;
  state.dcrDayReport = { screen: 1, ...action.payload };
  state.dcrDayReportError = false;
},
setDcrDayReportError: (state, action) => {
  state.dcrDayReportLoading = false;
  state.dcrDayReportError = true;
  state.dcrDayReportMsg = action.payload || 'Error fetching DCR day report';
  state.dcrDayReport = { screen: 1 };
},
clearDcrDayReport: (state) => {
  state.dcrDayReportLoading = false;
  state.dcrDayReport = { screen: 1 };
  state.dcrDayReportError = false;
  state.dcrDayReportMsg = '';
},

    // Reset entire state
    resetDcrReportState: () => dcrReportInitialState,
  },
});

// Export actions and reducer
export const dcrReportStateActions = dcrReportSlice.actions;
export const dcrReportStateReducer = dcrReportSlice.reducer;

// Export action type names for saga usage
export const fetchDivisionDataTypeName = fetchDivisionDataCreator().type;
export const fetchStaffDataTypeName = fetchStaffDataCreator({}).type;
export const fetchDcrReportDataTypeName = fetchDcrReportDataCreator({}).type;
export const fetchDcrDayReportDataTypeName = fetchDcrDayReportDataCreator({
  dcrDate: '',
  staffPositionId: '',
  staffUserId: ''
}).type;









